import { Icon, Skeleton, Text } from '@mntn/ui-components'
import type React from 'react'
import { AddBudgetPopover, Button, CreateCampaignOrExperimentButton } from '@/components/shared'
import type { CreateCampaignCallbacks, LayoutType } from '@/components/shared/types/types'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { useCreateCampaignHandler } from '@/hooks'
import { useAddBudget } from '@/hooks/api'

interface Props extends CreateCampaignCallbacks {
  orderItem: OrderItem
  handleToggleRow: () => void
  isExpanded: boolean
  layout?: LayoutType
  isLoading: boolean
  isDisabled: boolean
}

export function CampaignActions({
  orderItem,
  onCreateCampaignV2,
  handleToggleRow,
  isExpanded,
  layout = 'list',
  isLoading,
  isDisabled,
}: Props) {
  const { handleCreateCampaign, handleCreateExperiment, isCreating } = useCreateCampaignHandler({
    onCreateCampaignV2,
    orderItem,
  })
  const { mutateAsync: addBudget, isPending: isAddingBudget } = useAddBudget()

  const handleAddBudget = async ({ budget, offeringVersionId }: { budget: number; offeringVersionId: number }) => {
    return addBudget({
      orderItemId: orderItem.orderItemId,
      body: {
        advertiserId: orderItem.advertiserId,
        budget,
        offeringVersionId,
        audienceId: orderItem.audienceId ?? undefined,
        predictionId: orderItem.predictionId ?? undefined,
        isTest: orderItem.isTest,
      },
    })
  }

  if (layout === 'grid') {
    return (
      <div className="flex gap-2 w-full">
        {/* The grid column stretches its single item — the bare button when
            enabled, the tooltip's wrapper span when disabled — to the full
            width, so the disabled button fills the card row instead of
            collapsing to its label. */}
        <div className="grid grid-cols-1 flex-1">
          <CreateCampaignOrExperimentButton
            isLoading={isLoading || isCreating}
            isDisabled={isDisabled}
            onCreateCampaign={handleCreateCampaign}
            onCreateExperiment={handleCreateExperiment}
            orderItem={orderItem}
            buttonSize="tiny"
            className="border-none shadow-md px-4 w-full"
          />
        </div>
        <AddBudgetPopover
          orderItem={orderItem}
          isDisabled={isDisabled}
          onSubmit={handleAddBudget}
          isSubmitting={isAddingBudget}
        />
      </div>
    )
  }

  // List variant
  return (
    <div className="flex items-center gap-3">
      <Button
        size="small"
        variant="screen"
        onClick={handleToggleRow}
        disabled={orderItem.campaignGroups.length === 0}
        className="gap-2 px-4"
      >
        <Text inline variant="base-sb">
          Campaigns ({orderItem.campaignGroups.length})
        </Text>
        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} className="size-4 text-primary-accent" />
      </Button>
      <CreateCampaignOrExperimentButton
        isLoading={isLoading || isCreating}
        isDisabled={isDisabled}
        onCreateCampaign={handleCreateCampaign}
        onCreateExperiment={handleCreateExperiment}
        orderItem={orderItem}
        buttonSize="small"
        className="border-none"
      />
      <AddBudgetPopover
        orderItem={orderItem}
        isDisabled={isDisabled}
        size="small"
        onSubmit={handleAddBudget}
        isSubmitting={isAddingBudget}
      />
    </div>
  )
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const CampaignActionsSkeleton: React.FC<{
  layout?: LayoutType
}> = ({ layout = 'list' }) => {
  if (layout === 'grid') {
    return (
      <div className="flex flex-col w-full pt-4">
        <Skeleton dark className="w-full h-8" />
      </div>
    )
  }
  // List variant
  return (
    <div className="flex flex-col items-end gap-1">
      <Skeleton dark className="w-full h-[50px]" />
    </div>
  )
}
